import { Box, Typography } from "@mui/material";
import "./index.css";
import { useEffect, useRef } from "react";

const renderShortcutKeys = (shortcut) => {
  // Split on " + " or " or " to render each part as a badge
  const parts = shortcut.split(/(\s*\+\s*|\s+or\s+)/i);
  return (
    <span className="shortcut-keys">
      {parts.map((part, i) => {
        const trimmed = part.trim();
        if (!trimmed) return null;
        if (trimmed === "+" || trimmed.toLowerCase() === "or") {
          return (
            <span key={i} className="key-separator">
              {trimmed}
            </span>
          );
        }
        return (
          <kbd key={i} className="key-badge">
            {trimmed}
          </kbd>
        );
      })}
    </span>
  );
};

export const KeyboardShortcuts = () => {
  const sliderRef = useRef(null);
  const retailType = localStorage.getItem("retailType");
  const isRestaurant = retailType === "RESTAURANT";

  const sections = [
    {
      title: "Navigation Shortcuts",
      data: [
        { name: "Home Page", shortcut: "ctrl + h" },
        { name: "Cart Page", shortcut: "ctrl + m" },
        { name: "Customer Page", shortcut: "ctrl + k" },
        { name: "Order Page", shortcut: "ctrl + o" },
        { name: "Returns", shortcut: "ctrl + i" },
        { name: "Delivery Page", shortcut: "ctrl + d" },
        { name: "Setting Page", shortcut: "ctrl + s" },
        { name: "Hold Cart Page", shortcut: "ctrl + z" },
        { name: "Accounts", shortcut: "alt + 1" },
        { name: "Peripherals", shortcut: "alt + 2" },
        { name: "Payment Mode", shortcut: "alt + 3 or F5" },
        { name: "Performance Page", shortcut: "alt + 4" },
        // { name: "Display Mode", shortcut: "alt + 5" },
      ],
    },
    {
      title: "Cart Shortcuts",
      data: [
        { name: "New Cart", shortcut: "F4" },
        { name: "Hold Cart", shortcut: "F6" },
        { name: "Delete Cart", shortcut: "F7" },
        { name: "Apply Loyalty", shortcut: "F8" },
        { name: "Apply Coupon", shortcut: "F9" },
        { name: "Apply Offers", shortcut: "F10" },
        { name: "Search", shortcut: "F3" },
        { name: "Open Payment Module", shortcut: "F1" },
      ],
    },
    {
      title: "Miscellaneous Shortcuts",
      data: [
        // { name: "Toggle Theme", shortcut: "ctrl + b" },
        { name: "FullScreen", shortcut: "F11" },
        { name: "Exit FullScreen", shortcut: "F11" },
        { name: "Search", shortcut: "Esc" },
        { name: "Logout", shortcut: "alt + l" },
        { name: "Keyboard Shortcuts", shortcut: "ctrl + q" },
        { name: "Close Shortcut", shortcut: "Esc" },
        { name: "Previous Screen", shortcut: "shift + Esc" },
        { name: "Row Selection", shortcut: "alt + a" },
        // { name: "Product Details Search", shortcut: "alt + 6" },
      ],
    },
    ...(isRestaurant
      ? [
          {
            title: "Restaurant Cart Shortcuts",
            data: [
              { name: "Focus Search Bar", shortcut: "F3" },
              { name: "Navigate Products", shortcut: "↑ + ↓ + ← + →" },
              { name: "Add Focused Product to Cart", shortcut: "Enter" },
              { name: "Deselect Focused Product", shortcut: "Esc" },
              { name: "Filter: All", shortcut: "alt + a" },
              { name: "Filter: Veg", shortcut: "alt + v" },
              { name: "Filter: Non-Veg", shortcut: "alt + n" },
              { name: "Filter: Combos", shortcut: "alt + c" },
              { name: "Filter: Bestseller", shortcut: "alt + b" },
            ],
          },
          {
            title: "Restaurant Cart Item Shortcuts",
            data: [
              { name: "Focus Next Cart Item", shortcut: "alt + ↓" },
              { name: "Focus Previous Cart Item", shortcut: "alt + ↑" },
              { name: "Open Item Comment", shortcut: "Enter" },
              { name: "Increase Focused Item Qty", shortcut: "+ or =" },
              { name: "Decrease Focused Item Qty", shortcut: "-" },
              { name: "Place Order (with KOT print)", shortcut: "F2" },
              { name: "Place Order (skip KOT print)", shortcut: "shift + F2" },
              { name: "Print Bill", shortcut: "F5" },
              { name: "Create Order (skip print)", shortcut: "shift + F5" },
            ],
          },
          {
            title: "Table Layout Shortcuts",
            data: [
              { name: "Refresh Table Layout", shortcut: "F5" },
              { name: "Toggle Grid / Layout View", shortcut: "ctrl + g" },
              { name: "Navigate Tables", shortcut: "← + → + ↑ + ↓" },
              { name: "Select Focused Table", shortcut: "Enter" },
              { name: "Deselect / Clear Focus", shortcut: "Esc" },
              { name: "Filter: Available", shortcut: "ctrl + shift + 1" },
              { name: "Filter: Occupied", shortcut: "ctrl + shift + 2" },
              { name: "Filter: Order Placed", shortcut: "ctrl + shift + 3" },
              { name: "Filter: Payment Processing", shortcut: "ctrl + shift + 4" },
              { name: "Clear Filter", shortcut: "Esc" },
            ],
          },
        ]
      : []),
  ];

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        const scrollAmount = event.key === "ArrowLeft" ? -200 : 200;
        sliderRef.current?.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    };
    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, []);

  return (
    <Box className="keyboard-wrapper-box">
      <div className="keyboard-shortcuts-grid">
        {sections.map((section, index) => (
          <div key={index} className="shortcuts-card">
            <div className="shortcuts-card-header">
              <Typography className="shortcuts-card-title">{section.title}</Typography>
            </div>
            <div className="shortcuts-card-body">
              {section.data.map((shortcut, i) => (
                <div key={i} className="shortcut-row">
                  <span className="shortcut-name">{shortcut.name}</span>
                  {renderShortcutKeys(shortcut.shortcut)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Box>
  );
};
